'use client';
import { useEffect } from 'react';
 import Link from'next/link';
 import Header from'@/components/common/Header';
 import Footer from'@/components/common/Footer';
 import Icon from'@/components/ui/AppIcon';

const migrationApproaches = [
  {
    id: 'approach_greenfield',
    name: 'Greenfield',
    description: 'New implementation - clean slate',
    approach: 'Install S/4HANA from scratch, configure based on current business requirements, migrate master and transactional data selectively',
    pros: [
      'Clean system - no technical debt',
      'Latest best practices',
      'Simplified data model',
      'Opportunity for process reengineering',
    ],
    cons: [
      'Highest implementation effort',
      'Complete reconfiguration required',
      'Business disruption during cutover',
      'Loss of historical customizations',
    ],
    timeline: '12-24 months',
    complexity: 'High',
    riskLevel: 'High',
    bestFor: 'Organizations with heavily customized ECC systems or significant process changes needed',
  },
  {
    id: 'approach_brownfield',
    name: 'Brownfield',
    description: 'System conversion - technical upgrade',
    approach: 'In-place upgrade of existing ECC system to S/4HANA, preserving customizations, master data, and transaction history',
    pros: [
      'Fastest migration path',
      'Preserves all customizations',
      'Complete data history retained',
      'Lower business disruption',
    ],
    cons: [
      'Carries forward technical debt',
      'May require extensive custom code remediation',
      'Limited process redesign opportunity',
      'Potential performance issues from old data models',
    ],
    timeline: '6-12 months',
    complexity: 'Medium',
    riskLevel: 'Medium',
    bestFor: 'Organizations with well-functioning ECC systems and minimal need for process changes',
  },
  {
    id: 'approach_bluefield',
    name: 'Bluefield (Selective Data Transition)',
    description: 'Hybrid approach - best of both worlds',
    approach: 'New S/4HANA system with selective data migration and configuration carryover using tools like Shell Conversion or Landscape Transformation',
    pros: [
      'Balance of clean slate and preservation',
      'Selective technical debt elimination',
      'Flexible data migration strategy',
      'Opportunity for targeted improvements',
    ],
    cons: [
      'Most complex planning required',
      'Requires careful data selection',
      'Longer timeline than brownfield',
      'Specialized tooling and expertise needed',
    ],
    timeline: '9-18 months',
    complexity: 'High',
    riskLevel: 'Medium-High',
    bestFor: 'Organizations wanting process improvements while preserving critical historical data',
  },
]

const decisionCriteria = [
  {
    id: 'criteria_customization',
    factor: 'Customization Level',
    greenfield: 'High custom code → Good fit (fresh start)',
    brownfield: 'Low custom code → Good fit (preserve investments)',
    bluefield: 'Medium custom code → Good fit (selective cleanup)',
  },
  {
    id: 'criteria_process',
    factor: 'Process Changes Needed',
    greenfield: 'Significant → Required approach',
    brownfield: 'Minimal → Best approach',
    bluefield: 'Moderate → Balanced approach',
  },
  {
    id: 'criteria_timeline',
    factor: 'Timeline Pressure',
    greenfield: 'Not urgent → Feasible',
    brownfield: 'Urgent → Best option',
    bluefield: 'Moderate urgency → Feasible',
  },
  {
    id: 'criteria_data',
    factor: 'Historical Data Needs',
    greenfield: 'Archive acceptable → Feasible',
    brownfield: 'Full history required → Must have',
    bluefield: 'Selective history → Optimal',
  },
  {
    id: 'criteria_risk',
    factor: 'Risk Tolerance',
    greenfield: 'High tolerance → Acceptable',
    brownfield: 'Low tolerance → Preferred',
    bluefield: 'Medium tolerance → Balanced',
  },
]

const commonChallenges = [
  {
    id: 'challenge_debt',
    title: 'Technical Debt',
    description: 'Legacy custom code incompatible with S/4HANA simplified data model',
    mitigation: [
      'Run Custom Code Migration Worklist (SPAU/SPDD)',
      'Identify obsolete customizations for retirement',
      'Prioritize critical custom code for remediation',
      'Consider replacing with standard S/4 functionality',
    ],
    impact: 'High',
  },
  {
    id: 'challenge_data',
    title: 'Data Quality',
    description: 'Inconsistent, duplicate, or incomplete master and transactional data',
    mitigation: [
      'Execute data profiling and quality assessment',
      'Implement data cleansing programs pre-migration',
      'Define data governance policies',
      'Use SAP Master Data Governance tools',
    ],
    impact: 'High',
  },
  {
    id: 'challenge_testing',
    title: 'Testing Complexity',
    description: 'Comprehensive testing of integrated business processes in new environment',
    mitigation: [
      'Develop detailed test strategy covering all scenarios',
      'Implement automated testing where possible',
      'Conduct multiple migration rehearsals',
      'Plan for extended hypercare period',
    ],
    impact: 'Medium-High',
  },
  {
    id: 'challenge_change',
    title: 'Change Management',
    description: 'User adoption of new Fiori UX and changed business processes',
    mitigation: [
      'Early stakeholder engagement and communication',
      'Role-based training programs',
      'Super-user network for peer support',
      'Phased rollout with pilot groups',
    ],
    impact: 'Medium',
  },
]

export default function MigrationPathsPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="pt-24">
        {/* Page Header */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="reveal">
            <h1 className="font-serif text-display-md md:text-display-lg font-bold mb-4">
              Migration & Transformation Paths
            </h1>
            <p className="font-mono text-lg text-muted-foreground max-w-3xl">
              Strategic approaches for moving to S/4HANA. Compare greenfield, brownfield, and bluefield migration strategies.
            </p>
          </div>
        </section>

        {/* Migration Approaches */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Migration Approaches</h2>
            <p className="font-mono text-sm text-muted-foreground">Three strategic paths from ECC to S/4HANA</p>
          </div>

          <div className="space-y-8">
            {migrationApproaches.map((approach) => (
              <div key={approach.id} className="border border-border rounded p-8 reveal">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div>
                    <h3 className="font-serif text-2xl font-semibold mb-2">{approach.name}</h3>
                    <p className="font-mono text-sm text-muted-foreground mb-4">{approach.description}</p>
                    <p className="font-mono text-sm leading-relaxed">{approach.approach}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <span className="px-3 py-1 bg-muted rounded font-mono text-xs">
                      {approach.timeline}
                    </span>
                    <span className="px-3 py-1 bg-muted rounded font-mono text-xs">
                      {approach.complexity} complexity
                    </span>
                    <span className="px-3 py-1 bg-muted rounded font-mono text-xs">
                      {approach.riskLevel} risk
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-6">
                  <div>
                    <h4 className="font-mono text-xs uppercase text-success mb-3">Advantages</h4>
                    <ul className="space-y-2">
                      {approach.pros.map((pro, idx) => (
                        <li key={idx} className="font-mono text-sm flex items-start gap-2">
                          <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 shrink-0" variant="solid" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-mono text-xs uppercase text-warning mb-3">Challenges</h4>
                    <ul className="space-y-2">
                      {approach.cons.map((con, idx) => (
                        <li key={idx} className="font-mono text-sm flex items-start gap-2">
                          <Icon name="ExclamationCircleIcon" size={16} className="text-warning mt-0.5 shrink-0" variant="solid" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-6 border-t border-border">
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Best For</h4>
                  <p className="font-mono text-sm">{approach.bestFor}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Decision Framework */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Decision Framework</h2>
            <p className="font-mono text-sm text-muted-foreground">Key factors influencing approach selection</p>
          </div>

          <div className="overflow-x-auto reveal">
            <table className="table-research">
              <thead>
                <tr>
                  <th>Decision Factor</th>
                  <th>Greenfield</th>
                  <th>Brownfield</th>
                  <th>Bluefield</th>
                </tr>
              </thead>
              <tbody>
                {decisionCriteria.map((criteria) => (
                  <tr key={criteria.id}>
                    <td className="font-mono font-semibold">{criteria.factor}</td>
                    <td className="font-mono text-sm">{criteria.greenfield}</td>
                    <td className="font-mono text-sm">{criteria.brownfield}</td>
                    <td className="font-mono text-sm">{criteria.bluefield}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-6 bg-card border border-border rounded reveal">
            <h4 className="font-mono font-semibold mb-3 flex items-center gap-2">
              <Icon name="LightBulbIcon" size={20} className="text-primary" variant="outline" />
              Decision Guidance
            </h4>
            <p className="font-mono text-sm text-muted-foreground leading-relaxed">
              No single approach is universally correct. Selection depends on your organization's specific context: current system state, business transformation goals, timeline constraints, risk appetite, and available resources. Most large enterprises conduct detailed fit-gap analysis and proof-of-concept exercises before committing to an approach.
            </p>
          </div>
        </section>

        {/* Common Challenges */}
        <section className="mx-auto max-w-7xl px-6 py-16">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Common Challenges</h2>
            <p className="font-mono text-sm text-muted-foreground">Expected obstacles and mitigation strategies</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {commonChallenges.map((challenge) => (
              <div key={challenge.id} className="border border-border rounded p-6 reveal">
                <div className="flex items-start gap-3 mb-4">
                  <Icon name="ExclamationTriangleIcon" size={24} className="text-warning shrink-0" variant="outline" />
                  <div>
                    <h3 className="font-serif text-xl font-semibold mb-1">{challenge.title}</h3>
                    <p className="font-mono text-sm text-muted-foreground">{challenge.description}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-3">Mitigation Strategies</h4>
                  <ul className="space-y-2">
                    {challenge.mitigation.map((strategy, idx) => (
                      <li key={idx} className="font-mono text-sm flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 shrink-0"></span>
                        {strategy}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-border">
                  <span className="font-mono text-xs uppercase">
                    Impact: <span className="font-semibold">{challenge.impact}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Navigation CTA */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-t border-border">
          <div className="text-center space-y-6 reveal">
            <p className="font-mono text-muted-foreground">
              Explore specific concepts and terminology
            </p>
            <Link
              href="/a-z-index"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground font-mono text-sm rounded hover:opacity-90 transition-opacity"
            >
              A-Z Index
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}